/*
  AZP-7074 — mobile pagination dots.

  The Figma mobile frames for Recently Viewed (4227:16754) and Testimonials
  (4234:11294) show one full-width card per swipe with a row of 20x4 bars under
  it. Both sections already render the theme's `.scroller`, which snaps on its
  own, so only the indicator was missing.

  The theme's own indicator comes from Flickity via `layout: slider`, but that
  setting also rebuilds the desktop grid into a carousel; this element leaves
  the markup alone and just reads the scroll position.

  Usage:
    <div class="scroller">
      <div class="scroller-inner" id="Scroller-x"> … .grid > .grid-item … </div>
    </div>
    <bs-slider-dots for="Scroller-x" data-label="Load slide [index] of [total]">
    </bs-slider-dots>

  `data-item-selector` overrides what counts as one slide; it defaults to
  `.grid > .grid-item`.
*/
if (!customElements.get("bs-slider-dots")) {
  customElements.define(
    "bs-slider-dots",
    class BsSliderDots extends HTMLElement {
      connectedCallback() {
        this.track = document.getElementById(this.getAttribute("for"));

        if (!this.track) {
          this.toggleAttribute("hidden", true);
          return;
        }

        this.onScroll = this.onScroll.bind(this);
        this.track.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onScroll, { passive: true });

        // Recently Viewed replaces its cards once the search request comes
        // back, so the row has to be rebuilt rather than read once.
        const grid = this.track.querySelector(".grid");
        if (grid) {
          this.observer = new MutationObserver(() => this.render());
          this.observer.observe(grid, { childList: true });
        }

        this.render();
      }

      disconnectedCallback() {
        if (this.track) {
          this.track.removeEventListener("scroll", this.onScroll);
        }
        window.removeEventListener("resize", this.onScroll);
        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.frame) {
          cancelAnimationFrame(this.frame);
          this.frame = null;
        }
      }

      // One dot per swipe target. That is a card in the sections that scroll a
      // card at a time; "Shop Parts by Products" swipes a whole page of six
      // tiles, so it passes its own selector.
      get items() {
        return Array.from(
          this.track.querySelectorAll(this.getAttribute("data-item-selector") || ".grid > .grid-item"),
        );
      }

      // Distance from the track's scroll origin to the item's leading edge.
      // Measured rather than taken from `offsetLeft`, which is relative to
      // whichever ancestor happens to be positioned.
      offsetOf(item) {
        return (
          item.getBoundingClientRect().left -
          this.track.getBoundingClientRect().left +
          this.track.scrollLeft
        );
      }

      render() {
        const items = this.items;

        this.toggleAttribute("hidden", items.length < 2);

        if (items.length < 2) {
          this.replaceChildren();
          this.dots = null;
          return;
        }

        const label = this.getAttribute("data-label") || "";

        this.replaceChildren(
          ...items.map((item, index) => {
            const dot = document.createElement("button");
            dot.type = "button";
            dot.className = "bs-slider-dot";

            const text = document.createElement("span");
            text.className = "visually-hidden";
            text.textContent = label
              .replace("[index]", String(index + 1))
              .replace("[total]", String(items.length));
            dot.appendChild(text);

            dot.addEventListener("click", () => {
              this.track.scrollTo({ left: this.offsetOf(item), behavior: "smooth" });
            });

            return dot;
          }),
        );

        this.dots = Array.from(this.children);
        this.onScroll();
      }

      onScroll() {
        if (!this.dots || this.frame) {
          return;
        }

        this.frame = requestAnimationFrame(() => {
          this.frame = null;

          const items = this.items;
          if (items.length !== this.dots.length) {
            return;
          }

          const position = this.track.scrollLeft;
          let active = 0;
          let closest = Infinity;

          items.forEach((item, index) => {
            const distance = Math.abs(this.offsetOf(item) - position);
            if (distance < closest) {
              closest = distance;
              active = index;
            }
          });

          this.dots.forEach((dot, index) => {
            dot.classList.toggle("is-active", index === active);
            dot.setAttribute("aria-current", index === active ? "true" : "false");
          });
        });
      }
    },
  );
}
